/**
 * Reading the nouns out of a request.
 *
 * The split this file exists to make: A MODEL DECIDES WHAT KIND OF CHANGE IS
 * BEING ASKED FOR, AND THIS DECIDES WHAT IT IS BEING ASKED ABOUT. Routing a
 * sentence to "add a tool" is a judgement about language and a model is good
 * at it. Deciding that "the scanner" means LITH-03 is a lookup against a
 * catalogue the app already has in the reader's own language, and a model
 * that got it wrong would put a tool on the floor that nobody asked for.
 * So the catalogue does it, here, offline, in all four languages, and the
 * model never names a station.
 *
 * WHERE THE WORDS COME FROM. Almost all of them are already translated: a
 * station's name and the process it runs are in the i18n catalogue because
 * the approach card shows them. This indexes those, keeps only the words
 * that belong to exactly ONE station - "etcher" does, "system" does not -
 * and adds a short hand-written alias list per station for what a person
 * actually types ("scanner", "saw", "prober"). The alias lists are i18n keys
 * too, which is what makes this work in Chinese, where there are no spaces
 * to split on and the alias is the whole mechanism.
 *
 * Rebuilt when the language changes, because every phrase in it is a
 * translation.
 */

import { currentLanguage, tOr } from '../../i18n/i18n'
import { ARCHETYPES } from './archetypes'
import { nameOf, processOf, STATIONS, tagOf, ZONES, type FlowZone } from './catalogue'
import type { PlanHall } from '../../fab/floorPlan'

/** A script with no spaces in it: matched as a substring, not as a word. */
const CJK = /[㐀-鿿぀-ヿ가-힯]/

/** Words too short to mean anything on their own, in a language with spaces. */
const MIN_WORD = 3

/**
 * The request, twice: once raw and lowercased for substring matching, once
 * reduced to space-separated words with a space at each end so a word can be
 * looked for with its boundaries intact.
 */
interface Normalised {
  raw: string
  words: string
}

export function normalise(text: string): Normalised {
  const raw = text.toLowerCase().trim()
  // Letters, digits and accented Latin survive; everything else becomes a
  // gap, which is what turns "ETCH-01" and "etch 01" into the same thing.
  const words = ` ${raw.replace(/[^\p{L}\p{N}]+/gu, ' ').trim()} `
  return { raw, words }
}

/**
 * Where a phrase appears in a request, or -1.
 *
 * Both scripts are looked for in the SAME reduced string - punctuation gone,
 * single spaces, a space at each end - so two phrases' positions can be
 * compared whatever they are written in. A language with spaces gets word
 * boundaries, because "add" must not match inside "ladder"; a language
 * without them gets a plain substring, because there is no boundary to ask
 * for and the phrase is a word by construction.
 */
export function phraseIndex(n: Normalised, phrase: string): number {
  const p = phrase.trim().toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()
  if (!p) return -1
  if (CJK.test(p)) return n.words.indexOf(p)
  if (p.length < MIN_WORD) return -1
  const at = n.words.indexOf(` ${p} `)
  return at < 0 ? -1 : at + 1
}

const hit = (n: Normalised, phrase: string): boolean => phraseIndex(n, phrase) >= 0

/** A `|`-separated alias list from the catalogue. Empty when a language has none. */
const aliases = (key: string): string[] =>
  tOr(key, '')
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean)

/** The words of a name, for a language that has words. */
const wordsOf = (text: string): string[] =>
  CJK.test(text)
    ? []
    : text
        .toLowerCase()
        .split(/[^\p{L}\p{N}]+/u)
        .filter((w) => w.length >= MIN_WORD)

interface Index {
  lang: string
  /** Phrase -> station id, only for phrases belonging to exactly one station. */
  tools: Array<[string, string]>
  zones: Array<[string, FlowZone]>
  archetypes: Array<[string, string]>
}

let index: Index | null = null

/**
 * Build the phrase index for the language now loaded.
 *
 * Longest phrase first, so "wafer prep" is tried before "wafer" and a
 * two-word alias wins over one of its own halves.
 */
function buildIndex(): Index {
  // Two strengths of claim. A STRONG claim is something written on purpose
  // to name the station - its tag, its whole name, its whole process, an
  // alias. A WEAK one is a word that happens to be in its name.
  const strong = new Map<string, Set<string>>()
  const weak = new Map<string, Set<string>>()
  const claim = (to: Map<string, Set<string>>, phrase: string, id: string): void => {
    const p = phrase.trim().toLowerCase()
    if (!p) return
    const at = to.get(p) ?? new Set<string>()
    at.add(id)
    to.set(p, at)
  }

  for (const s of STATIONS) {
    // The asset tag, whole, and its stem: a person types "ETCH-01" or
    // "etch 01", and both reduce to the same words.
    claim(strong, s.assetTag, s.id)
    // Everything the catalogue already says about it, in this language.
    claim(strong, nameOf(s.id), s.id)
    claim(strong, processOf(s.id), s.id)
    for (const w of wordsOf(nameOf(s.id))) claim(weak, w, s.id)
    for (const w of wordsOf(processOf(s.id))) claim(weak, w, s.id)
    for (const a of aliases(`author.alias.${s.id}`)) claim(strong, a, s.id)
  }

  // A phrase two stations both answer to is no use to anybody: "wafer" is
  // in four names and "test" in two, and guessing between them is worse
  // than asking. Dropped, and the request falls through to the reply that
  // lists what the assistant can do.
  //
  // EXCEPT where exactly one of them claims it on purpose. "Scanner" is an
  // alias of LITH-03 and merely a word in EUV-22's name; "plasma" is ETCH-01's
  // by alias and the tin source's by accident. The deliberate claim wins -
  // otherwise every station added to the catalogue would quietly take words
  // away from the ones already in it.
  const tools: Array<[string, string]> = []
  for (const phrase of new Set([...strong.keys(), ...weak.keys()])) {
    const on = strong.get(phrase)
    const by = on && on.size > 0 ? on : weak.get(phrase)
    if (by && by.size === 1) tools.push([phrase, [...by][0]])
  }
  tools.sort((a, b) => b[0].length - a[0].length)

  const zones: Array<[string, FlowZone]> = []
  for (const z of ZONES) {
    for (const p of [tOr(`author.zone.${z}`, ''), ...aliases(`author.zoneAlias.${z}`)]) {
      if (p) zones.push([p.toLowerCase(), z])
    }
  }
  zones.sort((a, b) => b[0].length - a[0].length)

  const archetypes: Array<[string, string]> = []
  for (const a of ARCHETYPES) {
    for (const p of [tOr(`author.archetype.${a.id}.Name`, ''), ...aliases(`author.archetypeAlias.${a.id}`)]) {
      if (p) archetypes.push([p.toLowerCase(), a.id])
    }
  }
  archetypes.sort((a, b) => b[0].length - a[0].length)

  return { lang: currentLanguage(), tools, zones, archetypes }
}

function phrases(): Index {
  if (!index || index.lang !== currentLanguage()) index = buildIndex()
  return index
}

/** Forget the index - for a harness that switches language under it. */
export function resetMatcher(): void {
  index = null
}

/**
 * Every station named in a request, in the order the request names them, so
 * "put litho before etch" can be read as two tools rather than a set.
 *
 * A phrase that has already been consumed does not match again: "ETCH-01"
 * covers the characters "etch" as well, and counting both would add the
 * etcher twice and mask a second tool standing behind it.
 */
export function matchTools(text: string): string[] {
  const found: Array<{ id: string; at: number }> = []
  // Worked on a copy that gets holes punched in it: the phrases come
  // longest first, and each one that matches is replaced by spaces of its
  // own length so a shorter phrase inside it cannot match the same words,
  // while every later phrase keeps the position it would have had.
  const scratch: Normalised = normalise(text)
  for (const [phrase, id] of phrases().tools) {
    if (found.some((f) => f.id === id)) continue
    const at = phraseIndex(scratch, phrase)
    if (at < 0) continue
    found.push({ id, at })
    const p = phrase.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()
    scratch.words = scratch.words.slice(0, at) + ' '.repeat(p.length) + scratch.words.slice(at + p.length)
  }
  return found.sort((a, b) => a.at - b.at).map((f) => f.id)
}

/**
 * The stage of the flow a request names, and the words that named it.
 *
 * The phrase comes back because the caller has to be able to take it OUT of
 * the request before looking for tools. "Add the interconnect" names a zone
 * with a word that is also in ECD-12's process line, and without this the
 * request would add one tool out of the two the author asked for; "add an
 * etcher to the front end" names both a zone and a tool, and has to keep the
 * tool. Blanking the zone's own words settles both: what is left is a tool
 * only if the author named one.
 */
export function matchZoneAt(text: string): { zone: FlowZone; phrase: string } | null {
  const n = normalise(text)
  for (const [phrase, zone] of phrases().zones) if (hit(n, phrase)) return { zone, phrase }
  return null
}

/** The stage of the flow a request names, if it names one. */
export const matchZone = (text: string): FlowZone | null => matchZoneAt(text)?.zone ?? null

/** The request with one phrase struck out of it, for a second pass. */
export const withoutPhrase = (text: string, phrase: string): string =>
  text.replace(new RegExp(phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), ' ')

/** The fab a request asks to start from, if it asks for one. */
export function matchArchetype(text: string): string | null {
  const n = normalise(text)
  for (const [phrase, id] of phrases().archetypes) if (hit(n, phrase)) return id
  return null
}

/** Walled or open, if the request says. */
export function matchHall(text: string): PlanHall | null {
  const n = normalise(text)
  for (const p of aliases('author.hallAlias.ballroom')) if (hit(n, p)) return 'ballroom'
  for (const p of aliases('author.hallAlias.bayChase')) if (hit(n, p)) return 'bayChase'
  return null
}

/**
 * The name in "call it the pilot line".
 *
 * Quotes first, in all the shapes four languages quote with, because
 * somebody who quoted a name has told us exactly where it starts and stops.
 * Failing that, whatever follows a lead-in phrase from the catalogue - and
 * failing THAT, nothing, because a name guessed out of a sentence is a name
 * the author has to notice and undo.
 */
export function matchName(text: string): string | null {
  const quoted = /["“”'‘’「」『』]([^"“”'‘’「」『』]{1,60})["“”'‘’「」『』]/.exec(text)
  if (quoted) return quoted[1].trim() || null
  const lower = text.toLowerCase()
  for (const lead of aliases('author.renameLead')) {
    const at = lower.indexOf(lead.toLowerCase())
    if (at < 0) continue
    const rest = text.slice(at + lead.length).trim().replace(/^[:：,，]+/, '').trim()
    const name = cutName(rest)
    if (name) return name.slice(0, 60)
  }
  return null
}

/**
 * Where a name ends when nobody quoted it: at the first punctuation, or at
 * a conjunction from the catalogue - "call it Fab C and add CMP" is a name
 * and a second request, not a name with a request in it.
 */
function cutName(rest: string): string {
  let cut = rest.search(/[,，;；.。!！?？]/)
  if (cut < 0) cut = rest.length
  const lower = rest.toLowerCase()
  for (const stop of aliases('author.renameStop')) {
    const needle = CJK.test(stop) ? stop.toLowerCase() : ` ${stop.toLowerCase()} `
    const at = CJK.test(stop) ? lower.indexOf(needle) : ` ${lower} `.indexOf(needle) - 1
    if (at >= 0 && at < cut) cut = at
  }
  return rest.slice(0, cut).trim()
}

/** "Everything", "all of them" - a request about the whole catalogue at once. */
export function matchAll(text: string): boolean {
  const n = normalise(text)
  return aliases('author.allAlias').some((p) => hit(n, p))
}

/** "ETCH-01", for a reply that has to name what it did. */
export const tagList = (ids: string[]): string => ids.map(tagOf).join(', ')
